import '../styles/restScreens.css';
import { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../config/supabase';
import HoverHelp from './shared/HoverHelp';

const INDUSTRY_OPTIONS = [
    { value: 'Salon & Spa', category: 'scheduled' },
    { value: 'Barbershop', category: 'scheduled' },
    { value: 'Pet Grooming', category: 'scheduled' },
    { value: 'Pet Sitting/Boarding', category: 'scheduled' },
    { value: 'Physical Therapy', category: 'scheduled' },
    { value: 'Mental Health/Counseling', category: 'scheduled' },
    { value: 'Massage Therapy', category: 'scheduled' },
    { value: 'Chiropractic', category: 'scheduled' },
    { value: 'Personal Training/Fitness Coaching', category: 'scheduled' },
    { value: 'Tutoring/Education', category: 'scheduled' },
    { value: 'Home Cleaning', category: 'scheduled' },
    { value: 'Landscaping/Lawn Care', category: 'scheduled' },
    { value: 'Trade Contractor', category: 'scheduled' },
    { value: 'Handyman Services', category: 'scheduled' },
    { value: 'Photography', category: 'scheduled' },
    { value: 'Event Planning', category: 'scheduled' },
    { value: 'Veterinary Services', category: 'scheduled' },
    { value: 'Auto Repair/Detailing', category: 'scheduled' },
    { value: 'Interpreter/Translation Services', category: 'scheduled' },
    { value: 'Taxi/Rideshare', category: 'dispatch' },
    { value: 'Limousine/Livery', category: 'dispatch' },
    { value: 'Non-Emergency Medical Transport', category: 'dispatch' },
    { value: 'Courier/Delivery', category: 'dispatch' },
    { value: 'Moving Services', category: 'dispatch' },
    { value: 'Medical (In Development)', category: 'in-development' },
    { value: 'Dental (In Development)', category: 'in-development' },
    { value: 'Legal/Professional Services (In Development)', category: 'in-development' },
    { value: 'Other (please specify)', category: 'other' }
];

const CATEGORY_BADGE = {
    scheduled: '',
    dispatch: '',
    'in-development': 'In Development',
    other: ''
};

const DISPATCH_INDUSTRIES = new Set([
    'Taxi/Rideshare',
    'Limousine/Livery',
    'Non-Emergency Medical Transport',
    'Courier/Delivery',
    'Moving Services'
]);

const SPECIAL_FEATURE_QUESTIONS = [
    'Do you manage bookkeeping/accounting in-house?',
    'Do you need invoicing and payment collection?',
    'Do you need analytics or custom reporting?',
    "Does your service happen at the client's location — would live tracking matter to your customers?"
];

// Teams PR A: "Which tools does your team use?" Each choice is a built-in integration package. Choosing one only OFFERS it:
// its tile appears in the Admin Center for Tenant Admins, and it stays off for everyone until a Tenant Admin turns it on.
// Not choosing it changes nothing. (Saved by public.integration_opt_in; the database checks the caller is a Tenant Admin.)
export const TOOL_OPTIONS = [
    { key: 'integrations.microsoft_teams', label: 'Microsoft Teams', blurb: 'Mirror your Teams chats into IntelAgent (you choose who and what later).' },
];

const TIER_OPTIONS = [
    {
        name: 'Core',
        summaryLines: [
            'AI Attendant (customer-facing reservation intake, always-on)',
            'Basic SMS + Email tied to CRM contacts — manual send, no bulk',
            'Vertical pack baseline included free at this tier'
        ]
    },
    {
        name: 'Advanced',
        summaryLines: [
            'Everything in Core, plus:',
            'AI-assisted drafting + Analytics/reporting',
            'Bulk/campaign sends, automated sequences, two-way SMS keywords',
            'Deeper vertical pack capability unlocks at this tier'
        ]
    },
    {
        name: 'AI Compute',
        summaryLines: [
            'Everything in Core + Advanced, plus:',
            'Full system-level AI engagement, real algorithmic compute',
            'Analytics dashboard layer (bolted onto the free Dashboard shell)'
        ]
    }
];

// Module 19 (BL-380): the canonical value is 'Events'. Choosing it only records the industry; the Events pack itself
// stays dark until the events.vertical_pack capability is granted to the organization.
const EVENTS_INDUSTRIES = new Set([
    'Event Planning'
]);

export function deriveEngagementModel(industryCategory) {
    if (DISPATCH_INDUSTRIES.has(industryCategory)) return 'Dispatch';
    if (EVENTS_INDUSTRIES.has(industryCategory)) return 'Events';
    return 'Scheduled Services';
}

function getVerticalPackNote(engagementModel) {
    if (engagementModel === 'Dispatch') {
        return 'Your Dispatch pack baseline (assignment + tracking) is included free at every tier above. Deeper Dispatch capability unlocks at Advanced and AI Compute.';
    }
    if (engagementModel === 'Events') {
        return 'Your Events tools (event board, guests and check-in) are rolling out to Events businesses. We will turn them on for you when they are ready.';
    }

    return 'Your Scheduled Services baseline is included free at every tier above.';
}

function IndustryCombobox({
    inputValue,
    selectedValue,
    onInputChange,
    onSelect,
    isOtherSelected
}) {
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = useMemo(() => {
        const query = inputValue.trim().toLowerCase();
        if (!query) {
            return INDUSTRY_OPTIONS;
        }

        return INDUSTRY_OPTIONS.filter((option) => option.value.toLowerCase().includes(query));
    }, [inputValue]);

    return (
        <div className="relative">
            <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                    onInputChange(e.target.value);
                    setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onBlur={() => {
                    window.setTimeout(() => setIsOpen(false), 120);
                }}
                placeholder="Search industries"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                role="combobox"
                aria-expanded={isOpen}
                aria-controls="industry-category-options"
                aria-autocomplete="list"
            />
            {selectedValue && (
                <p className="mt-2 text-sm text-gray-600">
                    Selected: <span className="font-medium text-gray-800">{selectedValue}</span>
                    {isOtherSelected && ' — tell us your exact niche below.'}
                </p>
            )}
            {isOpen && (
                <div
                    id="industry-category-options"
                    role="listbox"
                    className="absolute z-20 mt-2 max-h-72 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg"
                >
                    {filteredOptions.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">No matches yet.</div>
                    ) : (
                        filteredOptions.map((option) => {
                            const isSelected = option.value === selectedValue;
                            const isInDevelopment = option.category === 'in-development';
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        onSelect(option.value);
                                        setIsOpen(false);
                                    }}
                                    className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition ${
                                        isSelected
                                            ? 'bg-orange-50 text-orange-900'
                                            : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className={isInDevelopment ? 'text-gray-500' : ''}>{option.value}</span>
                                    {CATEGORY_BADGE[option.category] && (
                                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                                            {CATEGORY_BADGE[option.category]}
                                        </span>
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

function OnboardingWizard({ user, orgId, onDismiss }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [finishError, setFinishError] = useState('');
    const [industrySearch, setIndustrySearch] = useState('');
    const [wizardState, setWizardState] = useState({
        industryCategory: '',
        industryOther: '',
        specialFeatureAnswers: SPECIAL_FEATURE_QUESTIONS.reduce((answers, question) => {
            answers[question] = null;
            return answers;
        }, {}),
        engagementModel: '',
        selectedTier: '',
        tools: []
    });

    useEffect(() => {
        if (wizardState.industryCategory && !industrySearch) {
            setIndustrySearch(wizardState.industryCategory);
        }
    }, [industrySearch, wizardState.industryCategory]);

    const ownerName = user?.user_metadata?.owner_name || 'there';
    const isOtherSelected = wizardState.industryCategory === 'Other (please specify)';
    const hasAnsweredAllSpecialFeatures = SPECIAL_FEATURE_QUESTIONS.every(
        (question) => typeof wizardState.specialFeatureAnswers[question] === 'boolean'
    );
    const hasSelectedTier = Boolean(wizardState.selectedTier);

    const handleIndustrySelect = (value) => {
        setWizardState((prev) => ({
            ...prev,
            industryCategory: value,
            industryOther: value === 'Other (please specify)' ? prev.industryOther : '',
            engagementModel: deriveEngagementModel(value)
        }));
        setIndustrySearch(value);
    };

    const handleSpecialFeatureAnswer = (question, answer) => {
        setWizardState((prev) => ({
            ...prev,
            specialFeatureAnswers: {
                ...prev.specialFeatureAnswers,
                [question]: answer
            }
        }));
    };

    const handleFinish = async () => {
        setFinishError('');
        if (!orgId) {
            setFinishError('Unable to save onboarding details: missing organization context.');
            return;
        }

        const resolvedIndustryCategory =
            wizardState.industryCategory === 'Other (please specify)'
                ? wizardState.industryOther
                : wizardState.industryCategory;

        const { error } = await supabaseClient.rpc('set_tenant_industry', {
            p_org_id: orgId,
            p_industry_category: resolvedIndustryCategory,
            p_industry_vertical: wizardState.engagementModel
        });

        if (error) {
            setFinishError(error.message || 'Unable to save onboarding details. Please try again.');
            return;
        }

        if (wizardState.tools.length > 0) {
            const { error: toolsError } = await supabaseClient.rpc('integration_opt_in', { p_keys: wizardState.tools });
            if (toolsError) {
                setFinishError(toolsError.message || 'Unable to save the tools your team uses. Please try again.');
                return;
            }
        }

        const payload = {
            industryCategory: wizardState.industryCategory,
            industryOther: wizardState.industryOther,
            specialFeatureAnswers: wizardState.specialFeatureAnswers,
            engagementModel: wizardState.engagementModel,
            selectedTier: wizardState.selectedTier,
            tools: wizardState.tools
        };

        console.log('[BL-170 Gate 4] Onboarding wizard state captured locally:', payload);
        onDismiss?.();
    };

    return (
        <div data-m6-layout="onboarding" data-m6-component="OnboardingWizard" className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 px-4 py-10">
            <div className="mx-auto w-full max-w-3xl rounded-3xl border border-orange-100 bg-white shadow-xl">
                <div className="border-b border-orange-100 px-8 py-6">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">Onboarding</p>
                            <h1 className="mt-2 text-3xl font-bold text-gray-900">Mission Central Command Setup</h1>
                        </div>
                        <div className="rounded-full bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700">
                            Step {currentStep} of 4
                        </div>
                    </div>
                </div>

                <div className="px-8 py-8">
                    {currentStep === 1 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900">Welcome to your Mission Central Command</h2>
                                <p className="mt-3 max-w-2xl text-base text-gray-600">
                                    {ownerName}, we&apos;ll take three quick steps to point the platform at the kind of operation you run.
                                </p>
                                <p className="mt-2 max-w-2xl text-base text-gray-600">
                                    Nothing is written to your tenant yet in this pass — this is just the onboarding shell getting its feet under it.
                                </p>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(2)}
                                    className="rounded-lg bg-orange-600 px-5 py-3 font-medium text-white transition hover:bg-orange-700"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900">What kind of business are you running?</h2>
                                <p className="mt-3 max-w-2xl text-base text-gray-600">
                                    Start typing and pick the closest match. Scheduled services appear first, then dispatch, then emerging verticals and fallback.
                                </p>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">Industry category</label>
                                <IndustryCombobox
                                    inputValue={industrySearch}
                                    selectedValue={wizardState.industryCategory}
                                    onInputChange={setIndustrySearch}
                                    onSelect={handleIndustrySelect}
                                    isOtherSelected={isOtherSelected}
                                />
                            </div>

                            {isOtherSelected && (
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-gray-700">Please specify</label>
                                    <input
                                        type="text"
                                        value={wizardState.industryOther}
                                        onChange={(e) => setWizardState((prev) => ({ ...prev, industryOther: e.target.value }))}
                                        placeholder="Tell us your niche"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                    />
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="rounded-lg border border-gray-300 px-5 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(3)}
                                    disabled={!wizardState.industryCategory || (isOtherSelected && !wizardState.industryOther.trim())}
                                    className="rounded-lg bg-orange-600 px-5 py-3 font-medium text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900">A few special-feature questions</h2>
                                <p className="mt-3 max-w-2xl text-base text-gray-600">
                                    Based on your industry, we&apos;ve set you up as a {wizardState.engagementModel} business.
                                </p>
                                {wizardState.engagementModel === 'Dispatch' && (
                                    <p className="mt-2 max-w-2xl text-base text-gray-600">
                                        The Dispatch Vertical Pack will be included in your setup.
                                    </p>
                                )}
                            </div>

                            <div className="space-y-5">
                                {SPECIAL_FEATURE_QUESTIONS.map((question) => {
                                    const answer = wizardState.specialFeatureAnswers[question];
                                    return (
                                        <div key={question} className="rounded-2xl border border-gray-200 p-5">
                                            <p className="text-base font-medium text-gray-900">{question}</p>
                                            <div className="mt-4 flex gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSpecialFeatureAnswer(question, true)}
                                                    className={`rounded-lg px-4 py-2 font-medium transition ${
                                                        answer === true
                                                            ? 'bg-orange-600 text-white'
                                                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSpecialFeatureAnswer(question, false)}
                                                    className={`rounded-lg px-4 py-2 font-medium transition ${
                                                        answer === false
                                                            ? 'bg-orange-600 text-white'
                                                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    No
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="rounded-2xl border border-gray-200 p-5" role="group" aria-labelledby="onboarding-tools-question">
                                <div className="flex flex-wrap items-center gap-2">
                                    <p id="onboarding-tools-question" className="text-base font-medium text-gray-900">Which tools does your team use?</p>
                                    <HoverHelp helpId="onboarding.tools" userId={user?.id} content={{ title: 'Team tools', body: 'Choosing Microsoft Teams offers its integration in Admin Center. It does not connect an account or mirror messages. A Tenant Admin decides whether to turn it on.' }} />
                                </div>
                                <p className="mt-1 text-sm text-gray-600">Optional. A tool you pick shows up in your Admin Center, switched off until an admin turns it on.</p>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    {TOOL_OPTIONS.map((tool) => {
                                        const chosen = wizardState.tools.includes(tool.key);
                                        return (
                                            <button
                                                key={tool.key}
                                                type="button"
                                                aria-pressed={chosen}
                                                title={tool.blurb}
                                                data-tool={tool.key}
                                                onClick={() => setWizardState((prev) => ({
                                                    ...prev,
                                                    tools: chosen ? prev.tools.filter((key) => key !== tool.key) : [...prev.tools, tool.key]
                                                }))}
                                                className={`rounded-lg px-4 py-2 font-medium transition ${
                                                    chosen
                                                        ? 'bg-orange-600 text-white'
                                                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {chosen ? '✓ ' : ''}{tool.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(2)}
                                    className="rounded-lg border border-gray-300 px-5 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(4)}
                                    disabled={!hasAnsweredAllSpecialFeatures}
                                    className="rounded-lg bg-orange-600 px-5 py-3 font-medium text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900">Choose your starting tier</h2>
                                <p className="mt-3 max-w-2xl text-base text-gray-600">
                                    Pick the tier that matches how much capability you want switched on in this onboarding path.
                                </p>
                            </div>

                            <div className="grid gap-4 md:grid-cols-3">
                                {TIER_OPTIONS.map((tier) => {
                                    const isSelected = wizardState.selectedTier === tier.name;
                                    return (
                                        <button
                                            key={tier.name}
                                            type="button"
                                            onClick={() => setWizardState((prev) => ({ ...prev, selectedTier: tier.name }))}
                                            className={`rounded-2xl border p-5 text-left transition ${
                                                isSelected
                                                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                                                    : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-sm'
                                            }`}
                                            aria-pressed={isSelected}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">{tier.name}</h3>
                                                    <p className="mt-2 text-sm font-medium text-gray-500">Pricing — coming soon</p>
                                                </div>
                                                <span
                                                    className={`mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                                                        isSelected
                                                            ? 'border-orange-500 bg-orange-500 text-white'
                                                            : 'border-gray-300 bg-white'
                                                    }`}
                                                >
                                                    {isSelected ? '✓' : ''}
                                                </span>
                                            </div>
                                            <ul className="mt-4 space-y-2 text-sm text-gray-700">
                                                {tier.summaryLines.map((line) => (
                                                    <li key={line} className="flex gap-2">
                                                        <span className="mt-0.5 text-orange-500">•</span>
                                                        <span>{line}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4 text-sm text-orange-900">
                                {getVerticalPackNote(wizardState.engagementModel)}
                            </div>
                            {finishError && (
                                <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                                    {finishError}
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(3)}
                                    className="rounded-lg border border-gray-300 px-5 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
                                >
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={handleFinish}
                                    disabled={!hasSelectedTier}
                                    className="rounded-lg bg-orange-600 px-5 py-3 font-medium text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Finish for now
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OnboardingWizard;
